import { useMemo } from 'react';
import cn from 'classnames';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import { MdDeleteForever } from 'react-icons/md';
import { PlanRow, TYPES_TO_NAMES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { IconButton } from '@Components/IconButton';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { SHORT_DATE_FORMAT } from '@src/utils/date-utils';
import { convert24hrTo12hr } from '@src/utils/time-utils';
import { formatLinearImpressionOverrides } from '../linear-buying-utils';

interface WeeksForNewRowProps {
  weekToKey: Record<string, string>;
  deleteNewRow: (key: string) => void;
}

const WeeksForNewRow = ({ weekToKey, deleteNewRow }: WeeksForNewRowProps) => (
  <div className='weeks-for-new-row'>
    {Object.keys(weekToKey).map(week => (
      <div key={week} className='week-chip'>
        <span>{R.pipe(Dfns.parseISO, Dfns.format(SHORT_DATE_FORMAT))(week)}</span>
        <IconButton
          size={Size.SM}
          appearance={Appearance.EMPTY}
          design={Intent.ERROR}
          onClick={() => deleteNewRow(weekToKey[week])}
        >
          <MdDeleteForever />
        </IconButton>
      </div>
    ))}
  </div>
);

interface PendingNewRowProps {
  rows: PlanRow[];
  deleteNewRow: (key: string) => void;
}

export const PendingNewRow = ({ rows, deleteNewRow }: PendingNewRowProps) => {
  const {
    avail,
    rotation,
    daypart_start,
    daypart_end,
    dow,
    type,
    length,
    count,
    cost,
    notes,
    creatives,
    impressionOverrides,
    orderedImpressions,
    orderedImpressionsDemo,
    orderedImpressionsMeasurement
  } = rows[0];

  const weekToKey = useMemo(
    () =>
      rows.reduce<Record<string, string>>(
        (prev, current) => ({ ...prev, [current.week]: current.key }),
        {}
      ),
    [rows]
  );

  return (
    <div className='new-row-container'>
      <div className='new-row-title'>
        <WeeksForNewRow weekToKey={weekToKey} deleteNewRow={deleteNewRow} />
        <span className='change-badge new'>New</span>
      </div>
      <div className='row-details'>
        <Detail label='Avail' value={avail} />
        <Detail label='Length' value={length} />
        <Detail label='Type' value={TYPES_TO_NAMES[`${type}`]} />
        <Detail label='Spots' value={count} />
        <Detail label='Rate' value={formatMoney(cost, 0)} />
        <Detail label='Spend' value={formatMoney(cost * count, 0)} />
        <Detail label='Start time' value={convert24hrTo12hr(daypart_start)} />
        <Detail label='End time' value={convert24hrTo12hr(daypart_end)} />
        <Detail label='Days' value={dow.join(', ')} />
        <Detail wide label='Rotation Label' value={rotation} />
        <Detail wide label='Notes' value={notes} />
        {creatives && (
          <Detail
            wide
            label='Creatives'
            value={creatives.map(({ isci, percent }) => `${isci} - ${percent}%`).join(', ')}
          />
        )}
        {orderedImpressionsDemo && (
          <Detail wide label='Ordered Impressions Demo' value={orderedImpressionsDemo} />
        )}
        {orderedImpressionsMeasurement && (
          <Detail
            wide
            label='Ordered Impressions Measurement'
            value={orderedImpressionsMeasurement}
          />
        )}
        {orderedImpressions && (
          <Detail wide label='Ordered Impressions' value={orderedImpressions} />
        )}
        {impressionOverrides && (
          <Detail
            wide
            label='Impression Overrides'
            value={formatLinearImpressionOverrides(impressionOverrides)}
          />
        )}
      </div>
    </div>
  );
};

interface DetailProps {
  label: string;
  value: string | number | null | undefined;
  /**
   * Takes the full width of the grid. For the handful of fields whose value is free text or a list
   * — everything else is short enough to sit several to a row.
   */
  wide?: boolean;
}

/** One label/value pair in a new row's detail grid. */
const Detail = ({ label, value, wide = false }: DetailProps) => (
  <div className={cn('detail', { wide })}>
    <span className='detail-label'>{label}</span>
    <span className='detail-value'>{value == null || value === '' ? '—' : value}</span>
  </div>
);
